import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Video, Calendar, Clock, Users, Plus, ChevronRight,
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';

const LiveSessionsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [sessionType, setSessionType] = useState('training');
  
  const isCoach = user?.role === 'coach';

  useEffect(() => {
    loadSessions();
    if (isCoach) {
      loadAthletes();
    }
  }, [isCoach]);

  const loadSessions = async () => {
    try {
      const res = await api.get('/live-sessions');
      setSessions(res.data);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAthletes = async () => {
    try {
      const res = await api.get('/coach/athletes');
      setAthletes(res.data);
    } catch (err) {
      console.error('Failed to load athletes:', err);
    }
  };

  const createSession = async () => {
    if (!selectedAthlete) {
      toast.error('Please select an athlete');
      return;
    }
    
    try {
      const res = await api.post('/live-sessions', {
        athlete_id: selectedAthlete,
        session_type: sessionType
      });
      toast.success('Session created!');
      setShowCreateModal(false);
      navigate(`/live-session/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create session');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'text-red-500 bg-red-500/20';
      case 'waiting': return 'text-yellow-500 bg-yellow-500/20';
      case 'scheduled': return 'text-blue-500 bg-blue-500/20';
      case 'completed': return 'text-green-500 bg-green-500/20';
      default: return 'text-zinc-500 bg-zinc-500/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'live': return <Video className="w-4 h-4" />;
      case 'waiting': return <Clock className="w-4 h-4" />;
      case 'scheduled': return <Calendar className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const activeSessions = sessions.filter(s => s.status === 'live' || s.status === 'waiting');
  const scheduledSessions = sessions.filter(s => s.status === 'scheduled');
  const pastSessions = sessions.filter(s => s.status === 'completed');

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="hpme-label text-primary mb-2">REAL-TIME COACHING</p>
            <h1 className="text-3xl md:text-4xl font-secondary font-bold tracking-tight mb-2">
              LIVE SESSIONS
            </h1>
            <p className="text-zinc-400">
              {isCoach ? 'Train your athletes with 3D pose analysis' : 'Connect with your coach for live training'}
            </p>
          </div>
          {isCoach && (
            <Button 
              className="hpme-btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-5 h-5 mr-2" />
              New Session
            </Button>
          )}
        </div>

        {/* Active Sessions */}
        {activeSessions.length > 0 && (
          <div className="mb-8">
            <h2 className="font-secondary font-bold text-lg mb-4 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              ACTIVE NOW
            </h2>
            <div className="space-y-3">
              {activeSessions.map(session => (
                <div 
                  key={session.id}
                  className="hpme-card p-4 border-red-500/30 hover:border-red-500/50 cursor-pointer"
                  onClick={() => navigate(`/live-session/${session.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-500/20 rounded-sm flex items-center justify-center">
                        <Video className="w-6 h-6 text-red-500" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          {isCoach ? session.athlete_name : `Coach ${session.coach_name}`}
                        </p>
                        <p className="text-sm text-zinc-500 capitalize">{session.session_type} Session</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-sm text-xs font-semibold ${getStatusColor(session.status)}`}>
                        {session.status.toUpperCase()}
                      </span>
                      <Button className="hpme-btn-primary" size="sm">
                        Join <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scheduled Sessions */}
        <div className="mb-8">
          <h2 className="font-secondary font-bold text-lg mb-4">UPCOMING</h2>
          {scheduledSessions.length === 0 ? (
            <div className="hpme-card p-8 text-center">
              <Calendar className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
              <p className="text-zinc-400 mb-2">No upcoming sessions</p>
              <p className="text-sm text-zinc-500">
                {isCoach ? 'Create a new session to train an athlete' : 'Your coach will schedule sessions for you'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledSessions.map(session => (
                <div 
                  key={session.id}
                  className="hpme-card p-4 hover:border-primary/30 cursor-pointer"
                  onClick={() => navigate(`/live-session/${session.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-sm flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          {isCoach ? session.athlete_name : `Coach ${session.coach_name}`}
                        </p>
                        <p className="text-sm text-zinc-500 capitalize">{session.session_type} Session</p>
                        {session.scheduled_at && (
                          <p className="text-xs text-zinc-500 mt-1">
                            {new Date(session.scheduled_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-mono text-zinc-500">{session.room_code}</span>
                      <ChevronRight className="w-5 h-5 text-zinc-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Past Sessions */}
        {pastSessions.length > 0 && (
          <div>
            <h2 className="font-secondary font-bold text-lg mb-4">COMPLETED</h2>
            <div className="space-y-3">
              {pastSessions.slice(0, 5).map(session => (
                <div 
                  key={session.id}
                  className="hpme-card p-4 opacity-70 hover:opacity-100"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-500/20 rounded-sm flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          {isCoach ? session.athlete_name : `Coach ${session.coach_name}`}
                        </p>
                        <p className="text-sm text-zinc-500">
                          {session.ended_at && new Date(session.ended_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-zinc-400">
                      View Recording
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Session Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="hpme-card p-6 max-w-md w-full">
              <div className="hpme-card-corner" />
              <h2 className="font-secondary font-bold text-xl mb-6">CREATE LIVE SESSION</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-500 mb-2 block">SELECT ATHLETE</label>
                  {athletes.length === 0 ? (
                    <div className="p-4 bg-zinc-800/50 rounded-sm text-center">
                      <Users className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                      <p className="text-sm text-zinc-500">No athletes connected</p>
                      <Button 
                        variant="link" 
                        className="text-primary text-sm"
                        onClick={() => {
                          setShowCreateModal(false);
                          navigate('/coach');
                        }}
                      >
                        Accept athlete requests first
                      </Button>
                    </div>
                  ) : (
                    <select
                      value={selectedAthlete}
                      onChange={(e) => setSelectedAthlete(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2"
                    >
                      <option value="">Choose an athlete...</option>
                      {athletes.map(athlete => (
                        <option key={athlete.id} value={athlete.athlete_id}>
                          {athlete.athlete_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                <div>
                  <label className="text-sm text-zinc-500 mb-2 block">SESSION TYPE</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['training', 'assessment', 'consultation'].map(type => (
                      <button
                        key={type}
                        onClick={() => setSessionType(type)}
                        className={`px-3 py-2 rounded-sm text-sm capitalize transition-colors ${
                          sessionType === type 
                            ? 'bg-primary text-black' 
                            : 'bg-zinc-800 text-zinc-400 hover:text-white'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button 
                  variant="ghost" 
                  className="flex-1"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="hpme-btn-primary flex-1"
                  onClick={createSession}
                  disabled={!selectedAthlete}
                >
                  Create Session
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LiveSessionsList;
